/**
 * Sidebar/utilities/index.ts
 * desc: utility functions for the v2 sidebar
 */

import { matchPath } from 'react-router';
import appUrls from '../../../settings/app-urls';
import { BOTTOM_ITEMS, DAILY_ITEMS, SETTINGS_ITEM, SETUP_GROUPS, type AppUrlKey, type NavItem } from '../config';

// every item a location can belong to, in the order the sidebar shows them
const ALL_ITEMS: NavItem[] = [ ...DAILY_ITEMS, ...SETUP_GROUPS.flatMap((group) => group.items), ...BOTTOM_ITEMS, SETTINGS_ITEM ];

/**
 * getNavItemPath
 * desc: the path an item links to
 */
export const getNavItemPath = (item: NavItem): string => appUrls[item.appUrlKey];

/**
 * isUnderAppUrl
 * desc: whether the location is the route's path or sits under it (segment-wise prefix match)
 */
export const isUnderAppUrl = (key: AppUrlKey, pathname: string): boolean =>
    matchPath({ path: appUrls[key], end: false }, pathname) !== null;

/**
 * isNavItemActive
 * desc: whether the location sits under the item's path or under any of its activeOn routes (prefix match,
 *       so detail pages light up their list item)
 */
export const isNavItemActive = (item: NavItem, pathname: string): boolean =>
    [ item.appUrlKey, ...(item.activeOn ?? []) ].some((key) => isUnderAppUrl(key, pathname));

/**
 * findOwningItem
 * desc: the navigation item a location belongs to — under its path or one of its activeOn routes — if any
 */
export const findOwningItem = (pathname: string): NavItem | undefined =>
    ALL_ITEMS.find((item) => isNavItemActive(item, pathname));
